import {Appointment} from 'externalAPIs/insurance-eligibility';
import {jwtDecode} from 'jwt-decode';
import {SurveyData} from 'screens/InsomniaSurvey/utils/surveyData';

interface JWTPayload {
  personId?: number;
  [key: string]: unknown;
}

const SURVEY_KEY_FRONT = 'insurance_card_front_uploaded';
const SURVEY_KEY_BACK = 'insurance_card_back_uploaded';

/**
 * Decodes a JWT access token and extracts the personId field.
 * @param accessToken - The JWT access token to decode
 * @returns The personId from the token, or null if the token is invalid or personId is missing
 */
export const getPersonIdFromToken = (accessToken: string | null): number | null => {
  if (!accessToken) {
    return null;
  }

  try {
    const decoded = jwtDecode<JWTPayload>(accessToken);
    if (decoded.personId && typeof decoded.personId === 'number') {
      return decoded.personId;
    }
    return null;
  } catch (error) {
    console.error('Error decoding JWT token:', error);
    return null;
  }
};

export const formatAppointmentStartTime = (dateString?: string): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const weekday = date.toLocaleDateString('en-US', {weekday: 'short'});
  const month = date.toLocaleDateString('en-US', {month: 'short'});
  const day = date.toLocaleDateString('en-US', {day: 'numeric'});
  const time = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  // Get timezone using Intl.DateTimeFormat
  const timezoneFormatter = new Intl.DateTimeFormat('en-US', {
    timeZoneName: 'short',
  });
  const timezoneParts = timezoneFormatter.formatToParts(date);
  const timezone = timezoneParts.find(part => part.type === 'timeZoneName')?.value ?? '';

  // Convert "10:00 AM" to "10:00a" format
  const timeFormatted = time.replace(/\s*(AM|PM)/i, match => {
    return match.toLowerCase().replace(' am', 'a').replace(' pm', 'p');
  });

  return `${weekday} ${month} ${day}, ${timeFormatted} (${timezone})`;
};

const INSURANCE_TAGS = {
  ELIGIBLE: 'ops:eligible_vob',
  INELIGIBLE: 'ops:ineligible_vob',
  PENDING: 'ops:pending_vob',
};

const APPOINTMENT_STATUS = {
  UPCOMING: 'Upcoming',
  CONFIRMED: 'Confirmed',
  CANCELLED: 'Cancelled',
};

export const STATUS_TYPES = {
  ELIGIBLE: 'confirmed',
  INELIGIBLE: 'ineligible',
  PENDING: 'pending',
  CANCELLED: 'cancelled',
  UPLOADED: 'uploaded',
};

export const getAppointmentStatus = (appointment: Appointment, tags: string[], cardUploadStatus: string) => {
  const appointmentStatus = appointment.status as keyof typeof APPOINTMENT_STATUS;

  if (appointmentStatus === APPOINTMENT_STATUS.CONFIRMED || appointmentStatus === APPOINTMENT_STATUS.UPCOMING) {
    if (tags?.includes(INSURANCE_TAGS.ELIGIBLE)) {
      return STATUS_TYPES.ELIGIBLE;
    }

    if (cardUploadStatus === 'missing') {
      return STATUS_TYPES.PENDING;
    }

    // Check if insurance cards were uploaded locally but we don't yet have
    // eligible or ineligible tags
    if (!tags?.includes(INSURANCE_TAGS.ELIGIBLE) && !tags?.includes(INSURANCE_TAGS.INELIGIBLE)) {
      const surveyData = SurveyData.read();
      const frontUploaded = surveyData?.[SURVEY_KEY_FRONT];
      const backUploaded = surveyData?.[SURVEY_KEY_BACK];
      if (frontUploaded && backUploaded) {
        return STATUS_TYPES.UPLOADED;
      }
    }

    if (tags?.includes(INSURANCE_TAGS.INELIGIBLE)) {
      return STATUS_TYPES.PENDING;
    }
    if (tags?.includes(INSURANCE_TAGS.PENDING)) {
      return STATUS_TYPES.PENDING;
    }
    return STATUS_TYPES.PENDING;
  }

  return STATUS_TYPES.CANCELLED;
};
